import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export interface CartItem {
  id: number;
  vendor_id?: number | null;
  barCode: string;
  productName: string;
  active_price: string | number;
  image_path?: string;
  variant?: string;
  quantity: number;
  default_discount_voucher_id?: number | null;
}

type AddToCartPayload = Omit<CartItem, "quantity"> & {
  quantity?: number;
};

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (product: AddToCartPayload) => void;
  addMultipleToCart: (products: CartItem[]) => void;
  removeFromCart: (id: number) => void;
  increaseQty: (id: number) => void;
  decreaseQty: (id: number) => void;
  clearCart: () => void;
  cartCount: number;
}

const CartContext = createContext<CartContextType | null>(null);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    const saved = localStorage.getItem("lyrazone_cart");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("lyrazone_cart", JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product: AddToCartPayload) => {
    const addedQty = Number(product.quantity || 1);

    setCartItems((prev) => {
      const existing = prev.find((item) => item.id === product.id);

      if (existing) {
        return prev.map((item) =>
          item.id === product.id
            ? {
                ...item,
                vendor_id: item.vendor_id ?? product.vendor_id ?? null,
                barCode: item.barCode || product.barCode,
                productName: item.productName || product.productName,
                active_price: item.active_price || product.active_price,
                image_path: item.image_path || product.image_path,
                variant: item.variant || product.variant,
                quantity: item.quantity + addedQty,
              }
            : item,
        );
      }

      return [
        ...prev,
        {
          ...product,
          vendor_id: product.vendor_id ?? null,
          quantity: addedQty,
        },
      ];
    });
  };

  const addMultipleToCart = (products: CartItem[]) => {
    setCartItems((prev) => {
      const updatedCart = [...prev];

      for (const product of products) {
        const existingIndex = updatedCart.findIndex(
          (item) => item.id === product.id,
        );

        if (existingIndex !== -1) {
          updatedCart[existingIndex] = {
            ...updatedCart[existingIndex],
            vendor_id:
              updatedCart[existingIndex].vendor_id ?? product.vendor_id ?? null,
            barCode: updatedCart[existingIndex].barCode || product.barCode,
            productName:
              updatedCart[existingIndex].productName || product.productName,
            active_price:
              updatedCart[existingIndex].active_price || product.active_price,
            image_path:
              updatedCart[existingIndex].image_path || product.image_path,
            variant: updatedCart[existingIndex].variant || product.variant,
            quantity:
              updatedCart[existingIndex].quantity +
              Number(product.quantity || 1),
          };
        } else {
          updatedCart.push({
            ...product,
            vendor_id: product.vendor_id ?? null,
            quantity: Number(product.quantity || 1),
          });
        }
      }

      return updatedCart;
    });
  };

  const removeFromCart = (id: number) => {
    setCartItems((prev) => prev.filter((item) => item.id !== id));
  };

  const increaseQty = (id: number) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, quantity: item.quantity + 1 } : item,
      ),
    );
  };

  const decreaseQty = (id: number) => {
    setCartItems((prev) =>
      prev
        .map((item) =>
          item.id === id ? { ...item, quantity: item.quantity - 1 } : item,
        )
        .filter((item) => item.quantity > 0),
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const cartCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems],
  );

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        addMultipleToCart,
        removeFromCart,
        increaseQty,
        decreaseQty,
        clearCart,
        cartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }

  return context;
};
